import React, { useMemo, useCallback } from "react";
import { Button } from "antd";
import { connect } from "dva";
import AlertWithBorder from "@/components/AlertWithBorder";
import CardWithHeader from "@/components/CardWithHeader";
import {
  DealerVariable,
  FixedByZip,
  PricingBands,
  CostPlus,
  NissanCityToCity,
  DealerResidential,
  TransporterVariable,
} from "@/components/Strategies";
import { EntityType, DealerStrategiesEnum, TransporterStrategiesEnum } from "../../constants";
import { checkIfStrategyChanged, checkIfStrategyIsValid } from "../../utils";
import InheritedTag from "../InheritedTag";
import StrategyPicker from "../StrategyPicker";

export const dealerStrategies = {
  [DealerStrategiesEnum.Variable]: props => <DealerVariable {...props} />,
  [DealerStrategiesEnum.FixedByZip]: props => <FixedByZip {...props} />,
  [DealerStrategiesEnum.PricingBands]: props => <PricingBands {...props} />,
  [DealerStrategiesEnum.CostPlus]: props => <CostPlus {...props} />,
  [DealerStrategiesEnum.NissanCityToCity]: props => <NissanCityToCity {...props} />,
  [DealerStrategiesEnum.Residential]: props => <DealerResidential {...props} />,
};
export const transporterStrategies = {
  [TransporterStrategiesEnum.Variable]: props => <TransporterVariable {...props} />,
  [TransporterStrategiesEnum.NissanCityToCity]: props => <NissanCityToCity {...props} />,
};

const StrategyCard = ({
  type,
  loading,
  inherited,
  strategyType,
  strategy,
  draftStrategy,
  onStrategyTypeChange,
  onStrategyChange,
  onSubmit,
  onDiscard,
  dispatch,
  loadingSubmit,
}) => {
  const title = useMemo(() => {
    if (type === EntityType.Dealer) {
      return "Dealer price";
    }
    if (type === EntityType.Transporter) {
      return "Transporter price";
    }

    return "";
  }, [type]);

  const onChange = useCallback(
    newValues => {
      if (typeof onStrategyChange === "function") {
        onStrategyChange(newValues);
      }
    },
    [onStrategyChange],
  );

  const children = useMemo(() => {
    if (type === EntityType.Dealer) {
      return dealerStrategies[strategyType]?.({
        type,
        inherited,
        strategy: draftStrategy,
        dispatch,
        onChange,
      });
    }
    if (type === EntityType.Transporter) {
      return transporterStrategies[strategyType]?.({
        type,
        inherited,
        strategy: draftStrategy,
        dispatch,
        onChange,
      });
    }

    return null;
  }, [type, strategyType, onChange, dispatch, draftStrategy, inherited]);

  const showActionButtons = useMemo(() => {
    const withChanges = checkIfStrategyChanged(strategy, draftStrategy);
    return withChanges;
  }, [strategy, draftStrategy]);

  const submitButtonDisabled = useMemo(() => {
    const valid = checkIfStrategyIsValid(draftStrategy);
    return !valid;
  }, [draftStrategy]);

  return (
    <CardWithHeader
      title={title}
      loading={loading}
      style={{ height: "100%" }}
      titleStyle={{
        fontSize: "32px",
      }}
      cardHeadStyles={{
        height: "auto",
      }}
      headerChildren={inherited ? <InheritedTag /> : null}
    >
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          padding: "24px",
          paddingBottom: showActionButtons ? "0px" : "24px",
        }}
      >
        <div
          style={{
            display: inherited ? "block" : "none",
            marginBottom: "24px",
          }}
        >
          {inherited && (
            <AlertWithBorder
              type="info"
              message="This strategy is inherited; you won't be able to make changes here."
            />
          )}
        </div>

        <StrategyPicker
          inherited={inherited}
          type={type}
          strategyType={strategyType}
          onStrategyTypeChange={onStrategyTypeChange}
        />
        {children}
      </div>

      <div
        style={{
          display: showActionButtons ? "flex" : "none",
          flexDirection: "row-reverse",
          marginBottom: "12px",
          marginRight: "24px",
          gap: "8px",
        }}
      >
        <Button key="save" type="primary" loading={loadingSubmit} disabled={submitButtonDisabled} onClick={onSubmit}>
          Save
        </Button>

        <Button key="discard" danger disabled={loadingSubmit} onClick={onDiscard}>
          Discard changes
        </Button>
      </div>
    </CardWithHeader>
  );
};

export default connect(({ loading }) => ({
  loadingSubmit: loading.effects["pricingStrategies/submitStrategies"],
}))(StrategyCard);
